/** Painel "Destaques" (tela L-03) — agrupado por seção do sumário.
 *
 * Agrupar por seção, e não por página, é o que faz a lista virar um índice de
 * estudo: "o que marquei no capítulo de Hereditariedade" é a pergunta do aluno,
 * não "o que marquei na página 47".
 */
import { useMemo } from 'react'

import { StudyNote } from '@/features/reader/components/StudyNote'

import { CORES } from '@/features/reader/hooks/useHighlights'
import { useReaderStore } from '@/features/reader/stores/readerStore'
import { sectionForPage } from '@/features/reader/utils/toc'
import type { BookAccess, Highlight } from '@/types/api'

const SEM_SECAO = '__sem_secao__'

interface HighlightsPanelProps {
  access: BookAccess
  destaques: Highlight[]
  onRemover: (id: string) => void
  carregando?: boolean
  notasHabilitadas?: boolean
}

function rotuloDaCor(cor: Highlight['color']): string {
  return CORES.find((c) => c.valor === cor)?.rotulo ?? cor
}

export function HighlightsPanel({
  access,
  destaques,
  onRemover,
  carregando = false,
  notasHabilitadas = false,
}: HighlightsPanelProps) {
  const irParaPagina = useReaderStore((s) => s.irParaPagina)

  const grupos = useMemo(() => {
    const porSecao = new Map<string, { titulo: string; itens: Highlight[] }>()
    for (const h of [...destaques].sort((a, b) => (a.page ?? 0) - (b.page ?? 0))) {
      const secao = h.page === null ? null : sectionForPage(access.toc, h.page)
      const chave = secao?.locator ?? SEM_SECAO
      const grupo = porSecao.get(chave) ?? {
        titulo: secao?.title ?? 'Sem seção no sumário',
        itens: [],
      }
      grupo.itens.push(h)
      porSecao.set(chave, grupo)
    }
    return [...porSecao.values()]
  }, [destaques, access.toc])

  if (carregando) return <p role="status">Carregando seus destaques…</p>

  if (destaques.length === 0) {
    return (
      <p className="reader-rightpanel__vazio" role="status">
        Selecione um trecho do livro e escolha uma cor para criar seu primeiro destaque.
      </p>
    )
  }

  return (
    <div className="highlights-panel">
      {grupos.map((grupo) => (
        <section key={grupo.titulo} className="highlights-panel__grupo">
          <h3>{grupo.titulo}</h3>
          <ul>
            {grupo.itens.map((h) => (
              <li key={h.id} className="highlights-panel__item">
                <span
                  className={`highlights-panel__cor highlights-panel__cor--${h.color}`}
                  aria-hidden="true"
                />
                <blockquote>{h.text_content}</blockquote>
                {notasHabilitadas && <StudyNote highlight={h} />}
                <div className="highlights-panel__acoes">
                  <button
                    type="button"
                    onClick={() => h.page && irParaPagina(h.page, access.page_count ?? undefined)}
                    disabled={h.page === null}
                    aria-label={`Ir para o destaque ${rotuloDaCor(h.color)} na página ${h.page ?? '—'}`}
                  >
                    p.{h.page ?? '—'} →
                  </button>
                  <button
                    type="button"
                    onClick={() => onRemover(h.id)}
                    aria-label={`Apagar destaque: ${h.text_content.slice(0, 40)}`}
                  >
                    Apagar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}

      <p className="highlights-panel__total">
        Total: {destaques.length} {destaques.length === 1 ? 'destaque' : 'destaques'} neste livro
      </p>
    </div>
  )
}
